import * as XLSX from 'xlsx'
import { ref } from '@vue/composition-api'
import i18n from '@/libs/i18n'
import { useLambda } from '@/views/habit/awsLambda'
import { hasPermission } from '@/composables/usePermission'
import { oid } from '@/helpers/mongoHelpers'
import store from '@/store'
import useMongoDBFunctions from '@/views/habit/useMongoDBFunctions'

export default function useImportRAT() {
  const { client } = store.state?.userStore?.userData
  const { createItem, getItemsAggregate } = useLambda()
  const { getLastCode } = useMongoDBFunctions()

  const isProcessing = ref(false)
  const validationResult = ref(null)
  const importResult = ref(null)

  // Valid options for catalogs
  const validRoles = ['responsible', 'inCharge', 'coResponsible']
  const validLegitimacion = ['Consentimiento', 'Contrato', 'Obligación legal', 'Interés vital', 'Interés público', 'Interés legítimo']
  const validPeriodicidad = ['Única', 'Periódica', 'Continua']
  const validOrigen = ['Del titular', 'De terceros', 'De fuentes públicas']
  const validSoporte = ['Papel', 'Digital', 'Mixto']
  const validFormato = ['Texto', 'Audio', 'Video', 'Imagen', 'Mixto']
  const validAmbito = ['national', 'international']
  const validVolumen = ['< 100', '100 - 1.000', '1.000 - 10.000', '10.000 - 100.000', '> 100.000']
  const validConservacion = ['< 1 año', '1 - 3 años', '3 - 5 años', '5 - 10 años', '> 10 años', 'Indefinido']

  const parseExcelFile = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()

      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result)
          const workbook = XLSX.read(data, { type: 'array' })

          // Look for RAT sheet
          const ratSheet = workbook.Sheets['RAT']
          if (!ratSheet) {
            reject(new Error('No se encontró la hoja "RAT" en el archivo'))
            return
          }

          // Convert to JSON, starting from row 4 (after headers)
          const jsonData = XLSX.utils.sheet_to_json(ratSheet, {
            range: 3, // Start from row 4 (0-indexed row 3)
            header: 'A',
            defval: ''
          })

          resolve(jsonData)
        } catch (error) {
          reject(error)
        }
      }

      reader.onerror = () => reject(new Error('Error al leer el archivo'))
      reader.readAsArrayBuffer(file)
    })
  }

  const validateRow = (row, rowIndex, departments, companies) => {
    const errors = []
    const warnings = []

    // Map Excel columns to fields (A=0, B=1, etc.)
    const id = row.A || ''
    const nombre = row.B || ''
    const entidad = row.C || ''
    const rol = row.D || ''
    const departamento = row.E || ''
    const finalidades = row.F || ''
    const tiposDatos = row.G || ''
    const hasSensitive = row.H || ''
    const hasMinors = row.I || ''
    const hasBiometric = row.J || ''
    const legitimacion = row.K || ''

    // Required field validations
    if (!nombre.trim()) {
      errors.push(`Fila ${rowIndex}: El campo "TRATAMIENTO DE DATOS" es obligatorio`)
    }

    if (!rol.trim()) {
      errors.push(`Fila ${rowIndex}: El campo "ROL DE LA ENTIDAD" es obligatorio`)
    } else {
      // Validate rol value
      const rolNormalized = normalizeRol(rol)
      if (!validRoles.includes(rolNormalized)) {
        errors.push(`Fila ${rowIndex}: Rol "${rol}" no válido. Valores permitidos: Responsable, Encargado, Corresponsable`)
      }
    }

    // Validate entidad (company)
    if (!entidad.trim()) {
      warnings.push(`Fila ${rowIndex}: No se especificó "ENTIDAD"`)
    } else {
      const companyExists = companies.find(c =>
        c.name.toLowerCase() === entidad.toLowerCase()
      )
      if (!companyExists) {
        warnings.push(`Fila ${rowIndex}: La entidad "${entidad}" no existe en el sistema`)
      }
    }

    // Validate departamento
    if (!departamento.trim()) {
      warnings.push(`Fila ${rowIndex}: No se especificó "DEPARTAMENTO PROPIETARIO"`)
    } else {
      // Check if department exists
      const deptExists = departments.find(d =>
        d.location.toLowerCase() === departamento.toLowerCase()
      )
      if (!deptExists) {
        warnings.push(`Fila ${rowIndex}: El departamento "${departamento}" no existe en el sistema`)
      }
    }

    if (!finalidades.trim()) {
      errors.push(`Fila ${rowIndex}: El campo "FINALIDADES DEL TRATAMIENTO" es obligatorio`)
    }

    if (!legitimacion.trim()) {
      errors.push(`Fila ${rowIndex}: El campo "LEGITIMACIÓN DEL TRATAMIENTO" es obligatorio`)
    } else if (!validLegitimacion.includes(legitimacion)) {
      errors.push(`Fila ${rowIndex}: Legitimación "${legitimacion}" no válida`)
    }

    // Validate boolean fields
    if (hasSensitive && !['Sí', 'No', 'Si'].includes(hasSensitive)) {
      errors.push(`Fila ${rowIndex}: "¿Tiene sensibles?" debe ser "Sí" o "No"`)
    }

    if (hasMinors && !['Sí', 'No', 'Si'].includes(hasMinors)) {
      errors.push(`Fila ${rowIndex}: "¿Tiene NNA?" debe ser "Sí" o "No"`)
    }

    if (hasBiometric && !['Sí', 'No', 'Si'].includes(hasBiometric)) {
      errors.push(`Fila ${rowIndex}: "¿Tiene biométricos?" debe ser "Sí" o "No"`)
    }

    // Validate catalog values if provided
    const periodicidad = row.N || ''
    if (periodicidad && !validPeriodicidad.includes(periodicidad)) {
      warnings.push(`Fila ${rowIndex}: Periodicidad "${periodicidad}" no está en el catálogo`)
    }

    const origen = row.O || ''
    if (origen && !validOrigen.includes(origen)) {
      warnings.push(`Fila ${rowIndex}: Origen "${origen}" no está en el catálogo`)
    }

    const formato = row.R || ''
    if (formato && !validFormato.includes(formato)) {
      warnings.push(`Fila ${rowIndex}: Formato "${formato}" no está en el catálogo`)
    }

    const ambito = row.S || ''
    if (ambito && !['Nacional', 'Internacional', 'national', 'international'].includes(ambito)) {
      warnings.push(`Fila ${rowIndex}: Ámbito "${ambito}" debe ser "Nacional" o "Internacional"`)
    }

    const volumen = row.T || ''
    if (volumen && !validVolumen.includes(volumen)) {
      warnings.push(`Fila ${rowIndex}: Volumen "${volumen}" no está en el catálogo`)
    }

    return { errors, warnings }
  }

  const normalizeRol = (rol) => {
    const rolLower = rol.toLowerCase().trim()
    if (rolLower === 'responsable') return 'responsible'
    if (rolLower === 'encargado') return 'inCharge'
    if (rolLower === 'corresponsable') return 'coResponsible'
    return rol
  }

  const normalizeAmbito = (ambito) => {
    const ambitoLower = ambito.toLowerCase().trim()
    if (ambitoLower === 'nacional') return 'national'
    if (ambitoLower === 'internacional') return 'international'
    return ambito
  }

  const validateFile = async (file) => {
    isProcessing.value = true
    validationResult.value = null

    try {
      // Parse Excel
      const rows = await parseExcelFile(file)

      // Get departments and companies from database for validation
      const [departments, companies] = await Promise.all([
        getItemsAggregate({
          collection: 'location',
          pipeline: [
            { $match: { client_id: oid(client.$oid), deleted: { $ne: true } } },
            { $project: { location: 1 } }
          ]
        }),
        getItemsAggregate({
          collection: 'company',
          pipeline: [
            { $match: { client_id: oid(client.$oid), deleted: { $ne: true } } },
            { $project: { name: 1 } }
          ]
        })
      ])

      const validRows = []
      const invalidRows = []
      const allErrors = []
      const allWarnings = []

      // Validate each row
      rows.forEach((row, index) => {
        const rowNum = index + 4 // Excel row number (starting from row 4)

        // Skip completely empty rows (all columns empty)
        const isCompletelyEmpty = Object.values(row).every(val => !val || val.toString().trim() === '')
        if (isCompletelyEmpty) return

        const { errors, warnings } = validateRow(row, rowNum, departments || [], companies || [])

        if (errors.length > 0) {
          invalidRows.push({ rowNum, data: row, errors, warnings })
          allErrors.push(...errors)
        } else {
          validRows.push({ rowNum, data: row, warnings })
          if (warnings.length > 0) {
            allWarnings.push(...warnings)
          }
        }
      })

      validationResult.value = {
        totalRows: rows.length,
        validRows: validRows.length,
        invalidRows: invalidRows.length,
        errors: allErrors,
        warnings: allWarnings,
        validData: validRows,
        invalidData: invalidRows
      }

      return validationResult.value
    } catch (error) {
      console.error('Error validating file:', error)
      throw error
    } finally {
      isProcessing.value = false
    }
  }

  const importTreatments = async () => {
    if (!hasPermission('privacy-treatment', 'create')) {
      throw new Error('No tienes permiso para importar tratamientos')
    }
    if (!validationResult.value || validationResult.value.validRows === 0) {
      throw new Error('No hay datos válidos para importar')
    }

    isProcessing.value = true
    importResult.value = null

    try {
      // Get departments and companies for lookups
      const [departments, companies] = await Promise.all([
        getItemsAggregate({
          collection: 'location',
          pipeline: [
            { $match: { client_id: oid(client.$oid), deleted: { $ne: true } } },
            { $project: { location: 1 } }
          ]
        }),
        getItemsAggregate({
          collection: 'company',
          pipeline: [
            { $match: { client_id: oid(client.$oid), deleted: { $ne: true } } },
            { $project: { name: 1 } }
          ]
        })
      ])

      // Get last treatment code for ID generation
      const lastCode = await getLastCode({
        collection: 'treatment',
        client_id: client.$oid
      })

      const importedTreatments = []
      const failedRows = []

      // Import each valid row
      for (let i = 0; i < validationResult.value.validData.length; i++) {
        const { rowNum, data } = validationResult.value.validData[i]

        try {
          const newCode = lastCode + i + 1
          const treatmentId = `T-${String(newCode).padStart(3, '0')}`

          // Find department ID
          const dept = departments?.find(d =>
            d.location.toLowerCase() === (data.E || '').toLowerCase()
          )

          // Find company ID if provided
          const company = companies?.find(c =>
            c.name.toLowerCase() === (data.C || '').toLowerCase()
          )

          // Parse tipos de datos (comma-separated)
          const tiposDatos = data.G ? data.G.split(',').map(t => t.trim()) : []

          // Build treatment payload
          const payload = {
            client_id: oid(client.$oid),
            treatmentId,
            nombre: data.B || '',
            empresa_id: company ? oid(company._id.toString()) : null,
            rol: normalizeRol(data.D || ''),
            departamento_id: dept ? oid(dept._id.toString()) : null,
            finalidades: data.F || '',
            tiposDatos,
            hasSensitive: ['Sí', 'Si'].includes(data.H),
            hasMinors: ['Sí', 'Si'].includes(data.I),
            hasBiometric: ['Sí', 'Si'].includes(data.J),
            legitimacion: data.K || '',
            sensiblesDescripcion: data.L || '',
            excepcionSensible: data.M || '',
            periodicidad: data.N || '',
            origen: data.O || '',
            medios: data.P || '',
            categoriaInteresados: data.Q || '',
            formato: data.R || '',
            ambito: normalizeAmbito(data.S || ''),
            volumen: data.T || '',
            plazoConservacion: data.U || '',
            terceros: {
              fuenteCedente: data.V || '',
              fuenteRegularizacion: data.W || '',
              destinatario: data.X || '',
              destinatarioFinalidad: data.Y || '',
              destinatarioLegitimacion: data.Z || ''
            },
            encargados: {
              identificacion: data.AA || '',
              prestacion: data.AB || ''
            },
            transferencias: {
              destinatario: data.AC || '',
              pais: data.AD || ''
            },
            resguardos: {
              papel: data.AE || '',
              sistemas: data.AF || ''
            },
            observaciones: data.AG || '',
            status: 'active'
          }

          await createItem({ collection: 'treatment', payload })
          importedTreatments.push(treatmentId)
        } catch (error) {
          console.error(`Error importing row ${rowNum}:`, error)
          failedRows.push({ rowNum, error: error.message })
        }
      }

      importResult.value = {
        success: true,
        imported: importedTreatments.length,
        failed: failedRows.length,
        failedRows
      }

      return importResult.value
    } catch (error) {
      console.error('Error importing treatments:', error)
      throw error
    } finally {
      isProcessing.value = false
    }
  }

  return {
    isProcessing,
    validationResult,
    importResult,
    validateFile,
    importTreatments
  }
}
